// The ARC benchmark figures of the Desk Dashboard: the labels of the ARC bands
// and the two return columns of the client table. The feature file of
// `wealth.portfolio.benchmarking.peer` and its cards share this module.
import type { DeskDashboardClientRow } from '@septima/contract';
import type { DataTableColumn } from '@/components/ui';
import { ABSENT, signedPct } from '@/lib/overview-figures';

// ARC band enum -> human label.
export const ARC_BAND_LABEL: Record<string, string> = {
    Cautious: 'Cautious',
    BalancedAsset: 'Balanced',
    SteadyGrowth: 'Steady Growth',
    EquityRisk: 'Equity Risk',
};

/** Green above zero, red below it, grey when the figure is absent. */
export function returnClass(value: number | null): string {
    if (value == null) return 'text-[rgb(var(--color-text-tertiary))]';
    return value >= 0 ? 'text-[rgb(var(--color-rag-green))]' : 'text-[rgb(var(--color-rag-red))]';
}

// The two return columns follow the window chip (SEP-200). The header carries
// the window, because the figure under it changes with the chip. The `id` does
// not carry the window: `DataTable` keys the sort state by `id`, so a constant
// id keeps an active sort when the reader changes the window.
export function performanceColumn(rangeLabel: string): DataTableColumn<DeskDashboardClientRow> {
    return {
        id: 'performance',
        header: rangeLabel,
        numeric: true,
        sortable: true,
        sortValue: (c) => c.rangeReturnPct ?? -Infinity,
        // The sort key uses a sentinel for a missing value. The filter reads the cell.
        filterValue: (c) => c.rangeReturnPct,
        cell: (c) => (
            <span className={returnClass(c.rangeReturnPct)}>{signedPct(c.rangeReturnPct)}</span>
        ),
    };
}

export function benchmarkColumn(rangeLabel: string): DataTableColumn<DeskDashboardClientRow> {
    return {
        id: 'benchmark',
        header: `Benchmark (ARC) ${rangeLabel}`,
        numeric: true,
        sortable: true,
        sortValue: (c) => c.benchmarkRangeReturnPct ?? -Infinity,
        filterValue: (c) => c.benchmarkRangeReturnPct,
        cell: (c) =>
            c.arcBand ? (
                <span>
                    {signedPct(c.benchmarkRangeReturnPct)}
                    <span className="ml-1.5 font-sans text-[rgb(var(--color-text-tertiary))]">
                        {ARC_BAND_LABEL[c.arcBand] ?? c.arcBand}
                    </span>
                </span>
            ) : (
                <span className="text-[rgb(var(--color-text-tertiary))]">{ABSENT}</span>
            ),
    };
}
